import {
  ForwardedRef,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { LayoutChangeEvent, View, ViewProps } from 'react-native';
import { Platform } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  OverlayProvider,
  useOverlay,
} from '@/common/components/Overlay/Overlay.context';
import {
  TBottomOverlayProps,
  TOverlayChildren,
  TOverlayRef,
} from '@/common/components/Overlay/Overlay.types';
import { useOnKeyPress } from '@/common/components/Overlay/Overlay.utils';
import { OverlayBackdrop } from '@/common/components/Overlay/OverlayBackdrop/OverlayBackdrop';
import { OverlayChildren } from '@/common/components/Overlay/OverlayChildren/OverlayChildren';
import { OverlayHeader } from '@/common/components/Overlay/OverlayHeader/OverlayHeader';
import { OverlayPortal } from '@/common/components/Overlay/OverlayPortal/OverlayPortal';
import { useKeyboard } from '@/common/hooks/useKeyboard';
import { spacing } from '@coverflex-tech/hypernova-design-tokens';
import BottomSheet, {
  BottomSheetFooter,
  BottomSheetFooterProps,
  BottomSheetHandleProps,
  BottomSheetScrollView,
  BottomSheetView,
} from '@gorhom/bottom-sheet';
import { styles } from './OverlayBottom.styles';

export const OverlayBottom = forwardRef(
  (
    {
      onClose,
      children,
      closeOnOutsideClick = true,
      header,
      footer,
      fixedHeight,
      backgroundColor,
      name = 'modal',
      hostName = 'overlay',
      backgroundComponent,
      scrollable = true,
      closeOnDismiss = true,
    }: TBottomOverlayProps,
    ref: ForwardedRef<TOverlayRef>
  ) => {
    const { top } = useSafeAreaInsets();
    const indexRef = useRef(-1);
    const bottomSheetRef = useRef<BottomSheet>(null);
    const { isKeyboardVisible, keyboardHeight } = useKeyboard();

    const handleSheetChanges = (index: number) => {
      indexRef.current = index;
      if (index === -1) {
        onClose();
      }
    };

    const onDismiss = useCallback(() => {
      if (indexRef.current === 0) {
        bottomSheetRef.current?.forceClose();
      }
    }, []);

    useOnKeyPress((evt) => {
      if (evt.key === 'Escape' && indexRef.current === 0) {
        bottomSheetRef.current?.close();
      }
    }, closeOnOutsideClick);

    useImperativeHandle(ref, () => ({
      close: onDismiss,
    }));

    const renderBackdrop = useCallback(() => {
      return (
        <OverlayBackdrop
          animated
          delay={false}
          onDismiss={closeOnOutsideClick ? onDismiss : undefined}
        />
      );
    }, [closeOnOutsideClick, onDismiss]);

    const renderBackground = useCallback(
      (props: ViewProps) => (
        <View
          style={props.style}
          className={styles.background({ backgroundColor })}
        />
      ),
      [backgroundColor]
    );

    const renderHandle = useCallback(
      (_props: BottomSheetHandleProps) => {
        return header ? <OverlayHeader>{header}</OverlayHeader> : undefined;
      },
      [header]
    );

    const renderFooter = useCallback(
      (props: BottomSheetFooterProps) => {
        return (
          <BottomSheetFooter {...props}>
            <View
              className={styles.footer({
                backgroundColor,
                keyboardVisible: isKeyboardVisible,
              })}
            >
              <OverlayChildren>{footer}</OverlayChildren>
            </View>
          </BottomSheetFooter>
        );
      },
      [footer, backgroundColor, isKeyboardVisible]
    );

    return (
      <OverlayPortal name={name} hostName={hostName}>
        <OverlayProvider onClose={onDismiss}>
          <View className={styles.container}>
            <BottomSheet
              ref={bottomSheetRef}
              onChange={handleSheetChanges}
              backdropComponent={renderBackdrop}
              enablePanDownToClose={closeOnDismiss}
              bottomInset={isKeyboardVisible ? keyboardHeight : undefined}
              enableDynamicSizing={!fixedHeight}
              snapPoints={fixedHeight ? ['100%'] : undefined}
              handleComponent={renderHandle}
              topInset={top + spacing.mobile['2xl']}
              backgroundComponent={renderBackground}
              footerComponent={renderFooter}
            >
              <OverlayBottomContent scrollable={scrollable}>
                {backgroundComponent && (
                  <View className={styles.backgroundComponent}>
                    {backgroundComponent}
                  </View>
                )}
                <OverlayChildren>{children}</OverlayChildren>
              </OverlayBottomContent>
            </BottomSheet>
          </View>
        </OverlayProvider>
      </OverlayPortal>
    );
  }
);
OverlayBottom.displayName = 'OverlayBottom';

export function OverlayBottomContent({
  children,
  scrollable = true,
}: {
  children: TOverlayChildren;
  scrollable?: boolean;
}) {
  const { onScroll } = useOverlay();
  const [height, setHeight] = useState<number | undefined>(
    Platform.OS === 'web' ? 0 : undefined
  );

  const onLayout = (event: LayoutChangeEvent) => {
    const newHeight = event.nativeEvent.layout.height;
    if (newHeight !== height) {
      setHeight(newHeight);
    }
  };

  if (!scrollable) {
    return (
      <BottomSheetView
        enableFooterMarginAdjustment
        className={Platform.OS === 'web' ? styles.view : undefined}
      >
        <OverlayChildren>{children}</OverlayChildren>
      </BottomSheetView>
    );
  }

  return (
    <BottomSheetScrollView
      enableFooterMarginAdjustment
      contentContainerStyle={{
        height,
      }}
      onScroll={onScroll}
    >
      <View onLayout={Platform.OS === 'web' ? onLayout : undefined}>
        <OverlayChildren>{children}</OverlayChildren>
      </View>
    </BottomSheetScrollView>
  );
}
